import Keycloak from 'keycloak-js';
import { KEYCLOAK_CONFIG } from '../config/env';
import { logger } from '../utils/logger';

// Environment-based Keycloak configuration
export const keycloak = new Keycloak({
  url: KEYCLOAK_CONFIG.url,
  realm: KEYCLOAK_CONFIG.realm,
  clientId: KEYCLOAK_CONFIG.clientId
});

// Helper functions
export const login = () => {
  // Always redirect back to app root after login
  // The app will then check sessionStorage for intended URL
  return keycloak.login({
    redirectUri: window.location.origin + '/'
  });
};

export const logout = () => keycloak.logout({ 
  redirectUri: window.location.origin 
});

export const isAuthenticated = () => keycloak.authenticated || false;
export const getToken = () => keycloak.token;
export const getUserInfo = () => {
  if (!keycloak.tokenParsed) return null;

  // Extract roles from realm_access.roles (matching backend logic)
  const realmRoles = keycloak.tokenParsed.realm_access?.roles || [];
  const appRoles = realmRoles.filter(role => 
    ['ADMIN', 'TEACHER', 'STUDENT'].includes(role)
  );

  return {
    id: keycloak.tokenParsed.sub,
    username: keycloak.tokenParsed.preferred_username,
    email: keycloak.tokenParsed.email,
    name: keycloak.tokenParsed.name,
    roles: appRoles  // Use filtered app roles
  };
};

// Token refresh promise to prevent multiple simultaneous refresh attempts
let tokenRefreshPromise: Promise<string | undefined> | null = null;

// Auto-refresh token before API calls
export const ensureValidToken = async (minValidity = 30) => {
  try {
    if (!keycloak.authenticated) {
      logger.log('Not authenticated, throwing error');
      throw new Error('Not authenticated');
    }

    const token = keycloak.token;
    const expiresAt = keycloak.tokenParsed?.exp;
    const nowSeconds = Math.floor(Date.now() / 1000);
    const secondsRemaining = expiresAt ? expiresAt - nowSeconds : 0;

    if (token && secondsRemaining > minValidity + 5) {
      return token;
    }
    
    // If there's already a refresh in progress, wait for it
    if (tokenRefreshPromise) {
      logger.log('⏳ Token refresh already in progress, waiting...');
      await tokenRefreshPromise;
      return keycloak.token;
    }
    
    // Start new refresh
    tokenRefreshPromise = keycloak.updateToken(minValidity).then((refreshed) => {
      if (refreshed) {
        logger.log('✅ Token refreshed, expires in:', keycloak.tokenParsed?.exp);
      } else {
        logger.log('✅ Token still valid, expires in:', keycloak.tokenParsed?.exp);
      }
      tokenRefreshPromise = null; // Clear the promise
      return keycloak.token;
    }).catch((error) => {
      tokenRefreshPromise = null; // Clear the promise on error
      throw error;
    });
    
    await tokenRefreshPromise;
    return keycloak.token;
  } catch (error) {
    logger.error('❌ Failed to refresh token:', error);
    // Don't auto-redirect to login, let auth guards handle it
    throw error;
  }
};

// Get highest role: ADMIN > TEACHER > STUDENT
export function getCurrentUserHighestRole(): 'ADMIN' | 'TEACHER' | 'STUDENT' | 'UNKNOWN' {
  const userInfo = getUserInfo();
  if (!userInfo || !userInfo.roles) return 'UNKNOWN';
  
  const roles = userInfo.roles;
  if (roles.includes('ADMIN') || roles.includes('ROLE_ADMIN')) return 'ADMIN';
  if (roles.includes('TEACHER') || roles.includes('ROLE_TEACHER')) return 'TEACHER';
  if (roles.includes('STUDENT') || roles.includes('ROLE_STUDENT')) return 'STUDENT';
  return 'UNKNOWN';
}